import { useMemo, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Eye, Users, PlayCircle, CheckCircle2, Mail, Percent, Trophy, ArrowUpDown, BookOpen } from "lucide-react";

export const PERIODS = [[7, "7 jours"], [30, "30 jours"], [90, "90 jours"], [0, "Depuis le début"]];
const ZERO = { visits: 0, uniques: 0, contacts: 0, plays: 0 };
const add = (a, b) => ({ visits: a.visits + b.visits, uniques: a.uniques + b.uniques, contacts: a.contacts + b.contacts, plays: a.plays + b.plays });
const rate = (r) => (r.uniques ? (r.contacts / r.uniques) * 100 : 0);
const pct = (v) => `${v.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %`;
const COLS = [["visits", "Visites"], ["uniques", "Visiteurs uniques"], ["contacts", "Demandes"], ["rate", "Conversion"]];
const MEDAL = ["bg-amber-100 text-amber-800 border-amber-300", "bg-slate-100 text-slate-700 border-slate-300", "bg-orange-50 text-orange-800 border-orange-200"];

const Kpi = ({ icon: I, label, value, sub, testid }) => (
  <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500"><I className="h-3.5 w-3.5" />{label}</div>
    <div className="text-2xl font-extrabold mt-1">{value}</div>
    {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
  </div>
);

export const PeriodPicker = ({ days, setDays }) => (
  <div className="flex gap-1 p-1 bg-slate-100 rounded-md text-xs font-bold" data-testid="xaltix-stats-period">
    {PERIODS.map(([d, l]) => <button key={d} type="button" data-testid={`xaltix-stats-period-${d}`} onClick={() => setDays(d)}
      className={`px-2.5 py-1 rounded transition-colors ${days === d ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>)}
  </div>
);

const Ranking = ({ posts, stats }) => {
  const [mode, setMode] = useState("sum");
  const [sort, setSort] = useState("visits");
  const rows = useMemo(() => {
    const list = posts.flatMap((p) => {
      const sq = { ...ZERO, ...stats[`li-${p.key}`] }, st = { ...ZERO, ...stats[`li-${p.key}-story`] };
      return mode === "sum" ? [{ id: p.key, label: p.label, ...add(sq, st) }]
        : [{ id: `${p.key}`, label: `${p.label} · fil`, ...sq }, { id: `${p.key}-story`, label: `${p.label} · story`, ...st }];
    }).map((r) => ({ ...r, rate: rate(r) }));
    return list.sort((a, b) => b[sort] - a[sort] || b.visits - a.visits);
  }, [posts, stats, mode, sort]);
  const podium = rows.filter((r) => r.visits || r.contacts).slice(0, 3);
  return (
    <div className="space-y-3" data-testid="xaltix-ranking">
      <div className="flex flex-wrap items-center gap-2">
        <Trophy className="h-4 w-4 text-amber-500" /><h3 className="font-bold text-sm">Classement des vidéos LinkedIn</h3>
        <div className="ml-auto flex gap-1 p-1 bg-slate-100 rounded-md text-xs font-bold">
          {[["sum", "Fil + story cumulés"], ["split", "Séparés"]].map(([k, l]) => <button key={k} type="button" data-testid={`xaltix-ranking-mode-${k}`} onClick={() => setMode(k)}
            className={`px-2.5 py-1 rounded ${mode === k ? "bg-white shadow-sm" : "text-slate-500"}`}>{l}</button>)}
        </div>
      </div>
      {podium.length > 0 ? (
        <div className="grid sm:grid-cols-3 gap-2" data-testid="xaltix-podium">
          {podium.map((r, i) => (
            <div key={r.id} className={`border rounded-lg p-3 ${MEDAL[i]}`} data-testid={`xaltix-podium-${i + 1}`}>
              <div className="text-[11px] font-extrabold">#{i + 1}</div>
              <div className="font-bold text-sm">{r.label}</div>
              <div className="text-xs mt-1">{r.visits} visite{r.visits > 1 ? "s" : ""} · {r.contacts} demande{r.contacts > 1 ? "s" : ""} · {pct(r.rate)}</div>
            </div>
          ))}
        </div>
      ) : <p className="text-xs text-slate-500" data-testid="xaltix-podium-empty">Aucune visite via un lien LinkedIn sur cette période.</p>}
      <div className="overflow-x-auto border border-slate-200 rounded-md">
        <table className="w-full min-w-[560px] text-sm" data-testid="xaltix-ranking-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500"><tr>
            <th className="text-left px-3 py-2">Vidéo</th>
            {COLS.map(([k, l]) => <th key={k} className="text-right px-3 py-2"><button type="button" data-testid={`xaltix-ranking-sort-${k}`} onClick={() => setSort(k)}
              className={`inline-flex items-center gap-1 ${sort === k ? "text-slate-900" : ""}`}>{l}<ArrowUpDown className="h-3 w-3" /></button></th>)}
          </tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={r.id} className="border-t border-slate-100" data-testid={`xaltix-ranking-row-${r.id}`}>
              <td className="px-3 py-2"><span className="text-slate-400 mr-2">{i + 1}.</span>{r.label}</td>
              <td className="px-3 py-2 text-right">{r.visits}</td><td className="px-3 py-2 text-right">{r.uniques}</td>
              <td className="px-3 py-2 text-right font-semibold">{r.contacts}</td><td className="px-3 py-2 text-right">{pct(r.rate)}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
};

export const XaltixStats = ({ overview: o, posts, stats }) => {
  if (!o) return null;
  const done = o.plays ? Math.round((o.completes / o.plays) * 100) : 0;
  const data = o.by_day.map((d) => ({ ...d, label: d.day.slice(8, 10) + "/" + d.day.slice(5, 7) }));
  return (
    <div className="space-y-5" data-testid="xaltix-stats">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Kpi icon={Eye} label="Visites" value={o.visits} sub="page XALTIX" testid="xaltix-kpi-visits" />
        <Kpi icon={Users} label="Visiteurs uniques" value={o.uniques} sub="par jour, sans cookie" testid="xaltix-kpi-uniques" />
        <Kpi icon={PlayCircle} label="Vidéo lancée" value={o.plays} sub="démonstration" testid="xaltix-kpi-plays" />
        <Kpi icon={CheckCircle2} label="Vue jusqu'au bout" value={o.completes} sub={`${done} % des lectures`} testid="xaltix-kpi-completes" />
        <Kpi icon={Mail} label="Demandes" value={o.contacts} sub="formulaire" testid="xaltix-kpi-contacts" />
        <Kpi icon={Percent} label="Conversion" value={pct(o.conversion)} sub="demandes / visiteurs" testid="xaltix-kpi-conversion" />
        <Kpi icon={BookOpen} label="Plaquette FR" value={o.brochure_fr ?? 0} sub="téléchargements page XALTIX" testid="xaltix-kpi-brochure-fr" />
        <Kpi icon={BookOpen} label="Plaquette EN" value={o.brochure_en ?? 0} sub="téléchargements page XALTIX" testid="xaltix-kpi-brochure-en" />
      </div>
      <div className="border border-slate-200 rounded-lg p-3" data-testid="xaltix-stats-chart">
        <div className="text-xs font-bold text-slate-600 mb-2">30 derniers jours</div>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={4} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
              <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="visits" name="Visites" stroke="#0B2A7A" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="plays" name="Lectures vidéo" stroke="#D9A441" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {posts && <Ranking posts={posts} stats={stats} />}
    </div>
  );
};
